import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { REPORT_LIMITS, type ReportAuthor, type ReportKind, type ReportStatus, type ReportView } from "@joogoos/contracts";
import { api, ApiError } from "../api.js";

/**
 * Your reports (ADR 0024): everything this workspace has reported, and each one's thread — what the operator said, what
 * moved it, and which version fixed it. The customer replies here rather than to an email, so the whole conversation
 * stays in one place, and closing is the one move that is theirs.
 */
export const STATUS_SAID: Record<ReportStatus, string> = {
  open: "open", looking: "looking at it", fixed: "fixed", closed: "closed", not_a_bug: "not a bug", wont_do: "won't do",
};
const KIND_SAID: Record<ReportKind, string> = { problem: "Problem", idea: "Idea", question: "Question" };
const AUTHOR_SAID: Record<ReportAuthor, string> = { customer: "You", operator: "Joogoos", system: "Joogoos" };

export function Reports({ selected, onSelect }: { selected: number | null; onSelect: (id: number | null) => void }) {
  return selected === null ? <ReportList onOpen={onSelect} /> : <ReportThread id={selected} onBack={() => onSelect(null)} />;
}

function StatusTag({ r }: { r: Pick<ReportView, "status" | "fixedInVersion"> }) {
  const tone = r.status === "fixed" ? "bg-good/12 text-good" : r.status === "open" || r.status === "looking" ? "bg-tint text-coral-2" : "bg-line text-muted";
  return (
    <span className={`shrink-0 px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wide ${tone}`}>
      {STATUS_SAID[r.status]}{r.fixedInVersion ? ` in ${r.fixedInVersion}` : ""}
    </span>
  );
}

function ReportList({ onOpen }: { onOpen: (id: number) => void }) {
  const q = useQuery({ queryKey: ["reports"], queryFn: api.reports });
  if (q.error) return <p role="alert" className="text-[13px] text-bad">{String(q.error)}</p>;
  if (!q.data) return <p className="text-[13px] text-muted">…</p>;
  const rows = q.data.reports;
  return (
    <div className="flex flex-col gap-3 max-w-[620px]">
      <p className="text-[12.5px] text-muted leading-relaxed">
        Everything this workspace has reported, newest first. A person reads every one, and what they say shows on the
        bell beside your credit — by email as well, if you switch that on in Settings.
      </p>
      {rows.length ? (
        <ul className="bg-panel border border-line rounded-[10px] divide-y divide-line">
          {rows.map((r) => (
            <li key={r.id}>
              <button onClick={() => onOpen(r.id)} className="w-full flex items-center gap-3 px-3.5 py-2.5 text-left hover:bg-page">
                <span className="text-[11.5px] font-mono text-muted shrink-0 w-12">R-{r.id}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[13.5px] truncate">{r.subject}</span>
                  <span className="block text-[11.5px] text-muted">{KIND_SAID[r.kind]} · {when(r.createdAt)}</span>
                </span>
                <StatusTag r={r} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[13px] text-muted py-6">
          Nothing reported yet. Help, the panel under this one, sends one, with the screen you were on.
        </p>
      )}
    </div>
  );
}

function ReportThread({ id, onBack }: { id: number; onBack: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["report", id], queryFn: () => api.report(id) });
  const [text, setText] = useState("");
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ["report", id] }), qc.invalidateQueries({ queryKey: ["reports"] })]);
  const reply = useMutation({ mutationFn: (body: string) => api.replyToReport(id, body), onSuccess: () => { setText(""); return refresh(); } });
  const close = useMutation({ mutationFn: () => api.closeReport(id), onSuccess: refresh });
  const back = <button onClick={onBack} className="self-start text-xs text-muted hover:text-ink">← All your reports</button>;

  if (q.error) {
    return (
      <div className="flex flex-col gap-3">
        {back}
        <p role="alert" className="text-[13px] text-bad">
          {q.error instanceof ApiError && q.error.status === 404 ? "There is no such report in this workspace." : String(q.error)}
        </p>
      </div>
    );
  }
  if (!q.data) return <p className="text-[13px] text-muted">…</p>;
  const { report: r, messages } = q.data;
  const closed = r.status === "closed";

  return (
    <div className="flex flex-col gap-4 max-w-[620px]">
      {back}
      <div>
        <div className="flex items-center gap-2">
          <span className="text-[11.5px] font-mono text-muted">R-{r.id}</span>
          <StatusTag r={r} />
        </div>
        <h3 className="text-[15px] font-semibold mt-1">{r.subject}</h3>
        <p className="text-[11.5px] text-muted">
          {KIND_SAID[r.kind]} · filed {when(r.createdAt)}{r.context.screen ? ` from ${r.context.screen}` : ""} · version {r.context.version}
        </p>
      </div>

      <ol className="flex flex-col gap-2.5" aria-label="The thread">
        <Entry who="You" at={r.createdAt} body={r.body} mine />
        {messages.map((m) => (
          <Entry key={m.id} who={AUTHOR_SAID[m.authorKind]} at={m.at} body={m.body} status={m.status} mine={m.authorKind === "customer"} />
        ))}
      </ol>

      {closed ? (
        <p className="text-[12.5px] text-muted leading-relaxed">
          This report is closed. If it comes back, file a new one from the screen where it happens.
        </p>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); if (text.trim()) reply.mutate(text.trim()); }} className="flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
            Reply
            <textarea
              value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={REPORT_LIMITS.body}
              placeholder={r.status === "fixed" ? "Still happening? Say so here and it will be looked at again." : "Anything to add."}
              className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-[13px] font-normal resize-y"
            />
          </label>
          <div className="flex items-center gap-2">
            <button type="submit" disabled={!text.trim() || reply.isPending} className="px-3 py-1.5 rounded-lg bg-ink text-white text-xs font-medium disabled:opacity-60">
              {reply.isPending ? "Sending…" : "Send reply"}
            </button>
            <button
              type="button" onClick={() => close.mutate()} disabled={close.isPending}
              className="ml-auto px-3 py-1.5 rounded-lg border border-line-2 bg-panel text-xs font-medium disabled:opacity-60"
            >
              Close this report
            </button>
          </div>
        </form>
      )}
      {reply.error || close.error ? <p role="alert" className="text-[12px] text-bad">{String(reply.error ?? close.error)}</p> : null}
    </div>
  );
}

function Entry({ who, at, body, status, mine }: { who: string; at: string; body: string; status?: ReportStatus | null | undefined; mine?: boolean | undefined }) {
  return (
    <li className={`rounded-[10px] border px-3.5 py-2.5 ${mine ? "bg-panel border-line" : "bg-tint/50 border-tint"}`}>
      <div className="flex items-baseline gap-2 text-[11.5px] text-muted">
        <b className="text-ink-2 font-semibold">{who}</b>
        {status ? <span>moved it to {STATUS_SAID[status]}</span> : null}
        <span className="ml-auto">{when(at)}</span>
      </div>
      <p className="text-[13px] mt-1 whitespace-pre-wrap leading-relaxed">{body}</p>
    </li>
  );
}

const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

export function when(iso: string) {
  const mins = Math.round((Date.now() - Date.parse(iso)) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return day(iso);
}
